import { Typography } from "@mui/material";
import styled, { css } from "styled-components";
import { responsive, THEME } from "../../styles/constants.style";

const fontVariants = (
  fontSize: string,
  fontWeight: number,
  fontFamily: string,
  mobileFontSize?: string,
) => css`
  font-size: ${fontSize};
  font-weight: ${fontWeight};
  font-family: ${fontFamily};

  ${mobileFontSize &&
  responsive.MOBILE`
    font-size: ${mobileFontSize};
  `}
`;

export const h1Regular = fontVariants(
  THEME.fontSizes[700],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
  THEME.fontSizes[650],
);

export const header = fontVariants(
  THEME.fontSizes[800],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
);

const h2Regular = fontVariants(
  THEME.fontSizes[600],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
  THEME.fontSizes[500],
);

export const h3Regular = fontVariants(
  THEME.fontSizes[400],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
);

export const h4Regular = fontVariants(
  THEME.fontSizes[500],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
  THEME.fontSizes[400],
);

export const h5Regular = fontVariants(
  THEME.fontSizes[300],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
);

export const body1Regular = fontVariants(
  THEME.fontSizes[500],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
);

const body2Regular = fontVariants(
  THEME.fontSizes[400],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
);

export const body3Regular = fontVariants(
  THEME.fontSizes[300],
  THEME.fontWeights["regular"],
  THEME.fontFamilies.primary,
);

export const tableHeader = fontVariants(
  THEME.fontSizes[500],
  THEME.fontWeights["medium"],
  THEME.fontFamilies.primary,
);

const fontWeightMedium = css`
  font-weight: ${THEME.fontWeights["medium"]};
`;

export const fontWeightBold = css`
  font-weight: ${THEME.fontWeights["bold"]};
`;

export const fontWeightSemiBold = css`
  font-weight: ${THEME.fontWeights["semiBold"]};
`;

export const buttonTypography = css`
  ${h4Regular}
  ${fontWeightBold}
`;

export const labelTypography = css``;

export const textFieldTypographyH4Regular = css`
  ${h4Regular}
`;

export const textFieldTypographyH3SemiBold = css`
  ${h3Regular}
  ${fontWeightSemiBold}
`;

export const textFieldTypographyH4Medium = css`
  ${h4Regular}
  ${fontWeightMedium}
`;

export const textFieldTypographyH5SemiBold = css`
  ${h5Regular}
  ${fontWeightSemiBold}
`;

export const TypographyWrapper = styled(Typography)`
  &.h1 {
    ${h1Regular}
  }

  &.headerBold,
  &.MuiTypography-headerBold {
    ${header}
    ${fontWeightBold}
  }

  &.h1-medium,
  &.MuiTypography-h1Medium,
  &.h1Medium {
    ${h1Regular}
    ${fontWeightMedium}
  }

  &.h1-bold,
  &.MuiTypography-h1Bold,
  &.h1Bold {
    ${h1Regular}
    ${fontWeightBold}
  }

  &.h1-semi-bold,
  &.MuiTypography-h1SemiBold,
  &.h1SemiBold {
    ${h1Regular}
    ${fontWeightSemiBold}
  }

  &.h2,
  &.MuiTypography-h2 {
    ${h2Regular}
  }

  &.h2-medium,
  &.MuiTypography-h2Medium,
  &.h2Medium {
    ${h2Regular}
    ${fontWeightMedium}
  }

  &.h2-bold,
  &.MuiTypography-h2Bold,
  &.h2Bold {
    ${h2Regular}
    ${fontWeightBold}
  }

  &.h2-semi-bold,
  &.MuiTypography-h2SemiBold,
  &.h2SemiBold {
    ${h2Regular}
    ${fontWeightSemiBold}
  }

  &.h3,
  &.MuiTypography-h3 {
    ${h3Regular}
  }

  &.h3-medium,
  &.MuiTypography-h3Medium,
  &.h3Medium {
    ${h3Regular}
    ${fontWeightMedium}
  }

  &.h3-bold,
  &.MuiTypography-h3Bold,
  &.h3Bold {
    ${h3Regular}
    ${fontWeightBold}
  }

  &.h3-semi-bold,
  &.MuiTypography-h3SemiBold,
  &.h3SemiBold {
    ${h3Regular}
    ${fontWeightSemiBold}
  }

  &.h4,
  &.MuiTypography-h4 {
    ${h4Regular}
  }

  &.h4-medium,
  &.MuiTypography-h4Medium,
  &.h4Medium {
    ${h4Regular}
    ${fontWeightMedium}
  }

  &.h4-bold,
  &.MuiTypography-h4Bold,
  &.h4Bold {
    ${h4Regular}
    ${fontWeightBold}
  }

  &.h4-semi-bold,
  &.MuiTypography-h4SemiBold,
  &.h4SemiBold {
    ${h4Regular}
    ${fontWeightSemiBold}
  }

  &.h5,
  &.MuiTypography-h5 {
    ${h5Regular}
  }

  &.h5-medium,
  &.MuiTypography-h5Medium,
  &.h5Medium {
    ${h5Regular}
    ${fontWeightMedium}
  }

  &.h5-bold,
  &.MuiTypography-h5Bold,
  &.h5Bold {
    ${h5Regular}
    ${fontWeightBold}
  }

  &.h5-semi-bold,
  &.MuiTypography-h5SemiBold,
  &.h5SemiBold {
    ${h5Regular}
    ${fontWeightSemiBold}
  }

  &.body1 {
    ${body1Regular}
  }

  &.body1-medium,
  &.MuiTypography-body1Medium,
  &.body1Medium {
    ${body1Regular}
    ${fontWeightMedium}
  }

  &.body1-bold,
  &.MuiTypography-body1Bold .body1Bold {
    ${body1Regular}
    ${fontWeightBold}
  }

  &.body1-semi-bold,
  &.MuiTypography-body1SemiBold .body1SemiBold {
    ${body1Regular}
    ${fontWeightSemiBold}
  }

  &.body2 {
    ${body2Regular}
  }

  &.body2-medium,
  &.MuiTypography-body2Medium,
  &.body2Medium {
    ${body2Regular}
    ${fontWeightMedium}
  }

  &.body2-bold,
  &.MuiTypography-body2Bold,
  &.body2Bold {
    ${body2Regular}
    ${fontWeightBold}
  }

  &.body2-semi-bold,
  &.MuiTypography-body2SemiBold,
  &.body2SemiBold {
    ${body2Regular}
    ${fontWeightSemiBold}
  }

  &.body3 {
    ${body3Regular}
  }

  &.body3-medium,
  &.MuiTypography-body3Medium,
  &.body3Medium {
    ${body3Regular}
    ${fontWeightMedium}
  }

  &.body3-bold,
  &.MuiTypography-body3Bold .body3Bold {
    ${body3Regular}
    ${fontWeightBold}
  }

  &.body3-semi-bold,
  &.MuiTypography-body3SemiBold .body3SemiBold {
    ${body3Regular}
    ${fontWeightSemiBold}
  }

  &.tableHeader {
    ${tableHeader};
  }
`;
